/**
 * @fileoverview Hook de UI Layer para verificar si existe texto en el mensaje del chat.
 * Permite determinar si el input de mensaje contiene texto válido para habilitar acciones como enviar o mostrar botones.
 * Implementa arquitectura optimizada con separación de capas, memoización y uso de TailwindCSS.
 *
 * @module useHasMessageText
 * @category Hook - UI Layer
 * @since 3.0.0
 * @version 3.1.0
 *
 * @typedef {Object} UseHasMessageTextParams
 * @property {string} message - Texto actual del input de mensaje
 *
 * @function useHasMessageText
 * @param {UseHasMessageTextParams} params - Parámetros del hook
 * @returns {boolean} Indica si el mensaje tiene texto válido
 *
 * @description
 * **Características:**
 * - Verifica si el input de mensaje contiene texto
 * - Útil para habilitar/deshabilitar botones de envío
 * - Memoización para performance
 * - Uso exclusivo en capa UI
 *
 * **Atomic Design:**
 * - Nivel: Hook - UI Layer
 *
 * @example
 * const hasText = useHasMessageText({ message });
 * if (hasText) { habilitar botón }
 *
 * @see {@link ChatInputMC} Componente input de chat
 * @see {@link ChatComponentMC} Componente principal de chat
 */
import { useEffect, useState } from "react";

const useHasMessageText = (textareaRef) => {
  const [hasText, setHasText] = useState(false);

  useEffect(() => {
    if (!textareaRef?.current) return;

    const handleInput = () => {
      const length = textareaRef.current.value.length;
      setHasText(length > 0);
    };

    textareaRef.current?.addEventListener('input', handleInput);

    return () => {
      textareaRef.current?.removeEventListener('input', handleInput);
    };
  }, [textareaRef]);

  return hasText;
};

export default useHasMessageText;
